import { forwardRef } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@/utils/cn';
import type { ButtonProps } from './Button.types';
import styles from './Button.module.scss';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      asChild = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const buttonClasses = cn(
      styles.button,
      styles[`button--${variant}`],
      styles[`button--${size}`],
      isLoading && styles['button--loading'],
      fullWidth && styles['button--full-width'],
      className
    );

    return (
      <Comp
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading}
        {...props}
        data-no-press-scale=""
      >
        {isLoading && (
          <span className={styles.button__spinner} aria-hidden="true">
            <svg
              className={styles.button__spinner__icon}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className={styles.button__spinner__circle}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
            </svg>
          </span>
        )}

        {!isLoading && leftIcon && (
          <span className={styles.button__icon} aria-hidden="true">
            {leftIcon}
          </span>
        )}

        {/*
          Con `asChild` (Slot) hay que marcar cuál es el hijo "real" a clonar con
          <Slottable>; si no, Slot recibiría varios hijos (spinner/iconos + texto)
          y `React.Children.only` peta. Sin `asChild` el <span> del texto se
          renderiza igual que siempre (Slottable es transparente).
        */}
        <Slottable>
          {asChild ? children : <span className={styles.button__text}>{children}</span>}
        </Slottable>

        {!isLoading && rightIcon && (
          <span className={styles.button__icon} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export default Button;
